import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MyHttpClient } from '../http-client';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export const SLAB_APPROVAL_PREVIEW_KEY_PREFIX = 'slab-approval-preview:';

export interface SlabApprovalPreviewContext {
  requestId: string;
  requestStatus?: string;
  operation?: string;
  createdBy?: string;
  statusChangedBy?: string;
  resourceId?: string;
  resource?: string;
  key?: string;
  version?: number;
  newValue: object;
  oldValue?: object;
}

@Injectable({
  providedIn: 'root'
})
export class SlabApprovalRequestsService {
  private readonly baseUrl = `${environment.server}/api/slab-approval-requests`;

  constructor(private http: MyHttpClient) { }

  fetchApprovalRequestById(requestId: string) {
    return this.http.get(`${this.baseUrl}/${requestId}`)
      .pipe(map((res: any) => res.data || res.response || res))
      .pipe(catchError(this.handleError));
  }

  fetchApprovalRequests(params: {
    pageNumber: number;
    pageLimit: number;
    requestStatus?: string;
    sortBy?: string;
    sortOrder?: number;
  }) {
    const query = new URLSearchParams({
      pageNumber: String(params.pageNumber),
      pageLimit: String(params.pageLimit),
    });
    if (params.requestStatus) {
      query.set('requestStatus', params.requestStatus);
    }
    if (params.sortBy) {
      query.set('sortBy', params.sortBy);
      query.set('sortOrder', String(params.sortOrder == null ? -1 : params.sortOrder));
    }
    return this.http.get(`${this.baseUrl}?${query.toString()}`)
      .pipe(map((res: any) => {
        const rows = res.data || res.response || [];
        return {
          data: rows,
          documentCount: res.documentCount != null ? res.documentCount : rows.length,
        };
      }))
      .pipe(catchError(this.handleError));
  }

  storePreviewForRequest(requestId: string, context: SlabApprovalPreviewContext | object) {
    const payload: SlabApprovalPreviewContext = (context as SlabApprovalPreviewContext).newValue != null
      ? (context as SlabApprovalPreviewContext)
      : { requestId, newValue: context as object };
    sessionStorage.setItem(
      `${SLAB_APPROVAL_PREVIEW_KEY_PREFIX}${requestId}`,
      JSON.stringify(payload),
    );
  }

  approveRequest(requestId: string, body: { resource: string; comment?: string }) {
    return this.http.post(`${this.baseUrl}/${requestId}/approve`, body)
      .pipe(map((res: any) => res))
      .pipe(catchError(this.handleError));
  }

  rejectRequest(requestId: string, body: { resource: string; comment?: string }) {
    return this.http.post(`${this.baseUrl}/${requestId}/reject`, body)
      .pipe(map((res: any) => res))
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Failed to fetch slab approval requests', error);
    return throwError(error);
  }
}
